import { BoxProps } from '@chakra-ui/layout';
import { cx } from '@chakra-ui/shared-utils';
import { Box, Image, Link } from '@telescope/cassini-ui';
import { useId } from 'react';

const adUnitSizeMap = {
  mediumRectangle: {
    width: 300,
    height: 250,
  },
  leaderboard: {
    width: 728,
    height: 90,
  },
  mobileLeaderboard: {
    width: 320,
    height: 50,
  },
  square: {
    width: 250,
    height: 250,
  },
  smallSquare: {
    width: 200,
    height: 200,
  },
  mobileBanner: {
    width: 300,
    height: 50,
  }
};

export type AdUnitSize = keyof typeof adUnitSizeMap;

export type AdUnitProps = 
  Omit<BoxProps, 'width' | 'height'> & {
  /**
   * The id of the ad unit container
   */
  id?: string;
  /**
   * If enabled, renders the ad unit container
   */
  isEnabled?: boolean;
  /**
   * Ad image url
   */
  imageUrl?: string;
  /**
   * Alt text for the ad image
   */
  altText?: string;
  /**
   * Click-through url for the ad
   */
  linkUrl?: string;
} & ({ 
  /**
   * Size of the ad unit
   */
    size: AdUnitSize;
  /**
   * If size is passed, custom width and height are not allowed
   */
    width?: never
    height?: never 
} | { 
  /**
   * Custom width
   */
  width: number
  /**
   * Custom height
   */
  height: number 
  /**
   * If width and height are passed, custom size is not allowed
   */
  size?: never;
}) 


/**
 * AdUnit - Renders a container for an ad unit of the specified AdUnitSize. If an imageUrl is provided, the ad unit will render an image with the specified altText and linkUrl, otherwise it will render its children (This is useful
 * for rendering a specific ad code snippet rather than an image).
 * @example
 * <AdUnit size="leaderboard" imageUrl="https://via.placeholder.com/728x90" altText="Ad placeholder" linkUrl="https://www.google.com" />
 * @example
 * <AdUnit size="leaderboard">      
 *      <ins
        className="adsbygoogle"
        data-ad-client="ca-pub-4591861188995436"
        data-ad-slot="4640466102"/>
    </AdUnit>
 */
export const AdUnit = ({
  id,
  size,
  imageUrl,
  isEnabled = false,
  altText = 'advertisement',
  linkUrl,
  children,
  width: customWidth,
  height: customHeight,
  ...rest
}: AdUnitProps) => {
  const { width = customWidth, height = customHeight } = size? adUnitSizeMap[size] : {};
  const uuid = useId();
  const classNames = cx('AdUnit', rest.className);

  if (!isEnabled) return null;

  const renderAdContent = () => {
    if (!imageUrl) return children;

    if (linkUrl && imageUrl) {
      return (
        <Link isExternal href={linkUrl}>
          <Image src={imageUrl} alt={altText} w="full" />
        </Link>
      );
    }

    return <Image src={imageUrl} alt={altText} w="full" />;
  };

  return (
    <Box
      className={classNames}
      id={id ?? `AdUnit_${size}_${width}x${height}_${uuid}`}
      overflow="hidden"
      {...rest}
      w={width}
      h={height}>
      {renderAdContent()}
    </Box>
  );
};
